/**
 * sortStepsChronologically — Pure function that orders a phase's trip steps
 * chronologically by their scheduled start time.
 *
 * Returns a new array (a permutation of the input) sorted in non-decreasing
 * order of `scheduledStart`. Ties (equal start times, or both unresolved) are
 * broken deterministically by ascending `id`, making the sort stable. The
 * input array is never mutated and the full set of steps is preserved.
 *
 * Requirements: 5.5
 */

/**
 * Convert a step's scheduledStart into a comparable numeric timestamp.
 * Date instances and numeric timestamps are supported. Unresolvable values
 * (null, undefined, invalid dates) sort after all resolvable times so that
 * undated steps trail the timeline rather than jumping to the front.
 *
 * @param {Date|number|null|undefined} value - The scheduledStart value
 * @returns {number} Milliseconds since epoch, or Infinity when unresolvable
 */
function toComparableTime(value) {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? Infinity : ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return Infinity;
}

/**
 * Sort trip steps non-decreasing by `scheduledStart`, stable by `id`.
 *
 * @param {Array<{id: string, scheduledStart: Date|number|null|undefined}>} steps
 *   The phase's trip steps to order.
 * @returns {Array} A new array containing the same steps in chronological order.
 */
export function sortStepsChronologically(steps) {
  if (!Array.isArray(steps)) return [];

  return [...steps].sort((a, b) => {
    const timeA = toComparableTime(a?.scheduledStart);
    const timeB = toComparableTime(b?.scheduledStart);

    if (timeA !== timeB) return timeA - timeB;

    // Stable tiebreaker: ascending id for deterministic ordering.
    const idA = a?.id == null ? '' : String(a.id);
    const idB = b?.id == null ? '' : String(b.id);
    if (idA < idB) return -1;
    if (idA > idB) return 1;
    return 0;
  });
}

export default sortStepsChronologically;
